import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { CheckIcon } from '@heroicons/react/24/solid';
import { Helmet } from 'react-helmet';
import { useMediaQuery } from 'react-responsive';

import useAccountQuery from 'api/queries/useAccountQuery';
import { BOYSENBERRY_S3_IMAGES } from 'constants';
import Button from 'honeydew/Button';
import { Drawer } from 'honeydew/Drawer';
import Modal from 'honeydew/Modal';
import useGlobalStore from 'hooks/useGlobalStore';
import useOrderMin from 'hooks/useOrderMin';
import useTailwindTheme from 'hooks/useTailwindTheme';
import { trackEvent } from 'utils/analyticsUtils';
import { getDay, localizeTimeZone } from 'utils/dateUtils';

import useCarouselAnimation from './useCarouselAnimation';
import useOnboardingModals from './useOnboardingModals';

/** Matches `duration-300` on the carousel viewport height transition */
const CAROUSEL_HEIGHT_TRANSITION_MS = 300;

function OpenWindowOnboardingModal() {
    const tailwindTheme = useTailwindTheme();
    const mobile = useMediaQuery({ maxWidth: tailwindTheme?.screens?.md });
    const {
        isMisfitsTheme,
        openWindowOnboardingModalOpen,
        setCartOpen,
        setClosedWindowOnboardingModalOpen,
        setOpenWindowOnboardingModalOpen,
    } = useGlobalStore();

    const { closeOpenWindowModal } = useOnboardingModals({
        setClosedWindowOnboardingModalOpen,
        setOpenWindowOnboardingModalOpen,
    });

    const { data: account } = useAccountQuery();
    const { marketCloseDate, marketOpenDate } = account ?? {};
    const { orderMin } = useOrderMin();

    const {
        currentHeight,
        emblaRef,
        getSlideStyle,
        remeasureSlides,
        scrollNext,
        scrollTo,
        selectedIndex,
        slidesContainerRef,
    } = useCarouselAnimation();

    useEffect(() => {
        if (openWindowOnboardingModalOpen) {
            trackEvent('open window onboarding modal viewed', { slide_number: selectedIndex + 1 });
        }
    }, [openWindowOnboardingModalOpen, selectedIndex]);

    const openDay = marketOpenDate?.date ? getDay(marketOpenDate.date) : null;
    const openTime = marketOpenDate?.date ? localizeTimeZone(marketOpenDate.date, true)?.replace(':00', '') : null;
    const closeDay = marketCloseDate?.date ? getDay(marketCloseDate.date) : null;
    const closeTime = marketCloseDate?.date ? localizeTimeZone(marketCloseDate.date, true)?.replace(':00', '') : null;

    const slides = [
        {
            key: 'prefilled-cart',
            content: (
                <div className="overflow-hidden rounded-xl border border-black bg-white">
                    <div className="h-[200px] w-full shrink-0 overflow-hidden md:h-[260px]">
                        <img
                            src={`${BOYSENBERRY_S3_IMAGES}onboarding-step1.jpg`}
                            alt="Every week, we pre-fill your cart with personalized picks"
                            className="h-full w-full object-cover if:origin-top if:scale-[1.22]"
                            onLoad={remeasureSlides}
                        />
                    </div>
                    <div className="relative z-10 flex flex-col items-center p-15 text-center">
                        <h3 className="mb-10 font-grotesk-bold text-body-mobile-md md:text-body-desktop-lg">
                            Every week, we pre-fill your cart with personalized picks
                        </h3>
                        <p className="text-body-mobile-xs text-grey md:text-body-desktop-sm">
                            You can always make changes or start from scratch. When your shopping window closes, we ship
                            your box with the items in your cart.
                        </p>
                    </div>
                </div>
            ),
        },
        {
            key: 'customize-cart',
            content: (
                <div className="overflow-hidden rounded-xl border border-black bg-white">
                    <Helmet>
                        <script
                            type="text/javascript"
                            src="https://addevent.com/libs/atc/1.6.1/atc.min.js"
                            async
                            defer
                        />
                    </Helmet>
                    <img
                        src={`${BOYSENBERRY_S3_IMAGES}onboarding-step2.jpg`}
                        alt="Customize your cart during your shopping window"
                        className="h-[200px] w-full object-cover md:h-[260px]"
                        onLoad={remeasureSlides}
                    />
                    <div className="flex flex-col items-center p-15 text-center">
                        <div className="mb-15 w-full rounded-lg bg-mango px-15 py-10 text-center">
                            <p className="text-body-xs flex items-center justify-center gap-5 font-grotesk-bold">
                                <CheckIcon className="h-[14px] w-[14px] shrink-0" />
                                YOUR SHOPPING WINDOW IS OPEN
                            </p>
                            {openDay && openTime && closeDay && closeTime && (
                                <p className="text-body-xs">
                                    Shop weekly: {openDay} {openTime} - {closeDay} {closeTime}
                                </p>
                            )}
                        </div>
                        <h3 className="mb-10 font-grotesk-bold text-body-mobile-md md:text-body-desktop-lg">
                            Customize your cart during your shopping window
                        </h3>
                        <p className="text-body-mobile-xs text-grey md:text-body-desktop-sm">
                            Edit, add, or delete any items in your cart
                            {closeDay && closeTime && (
                                <>
                                    {' '}
                                    before{' '}
                                    <span className="font-grotesk-bold text-black">
                                        {closeDay} at {closeTime}
                                    </span>
                                </>
                            )}{' '}
                            to ensure you get everything you want and need.
                        </p>
                        {marketOpenDate?.date && marketCloseDate?.date && (
                            <p className="mt-15 text-body-mobile-xs md:text-body-desktop-sm">
                                <style>
                                    {`
                                        .calendar-link-inline.addeventatc {
                                            border: none !important;
                                            background: transparent !important;
                                            box-shadow: none !important;
                                            padding: 0 !important;
                                            display: inline !important;
                                            font-weight: 100 !important;
                                            font-family: 'Grotesk Regular', sans-serif;
                                            font-size: 12px;
                                            @media (min-width: 768px) {
                                                font-size: 14px;
                                            }
                                        }
                                        .calendar-link-inline.addeventatc:hover {
                                            background: transparent !important;
                                        }
                                    `}
                                </style>
                                <span
                                    title="Add to Calendar"
                                    className="calendar-link-inline addeventatc"
                                    data-intel="false"
                                    data-google-api="true"
                                    data-dropdown-y="up"
                                >
                                    <span className="text-black underline">Click here to add a calendar reminder</span>
                                    <span className="start">{marketOpenDate.date}</span>
                                    <span className="end">{marketCloseDate.date}</span>
                                    <span className="timezone">{marketOpenDate.timezone}</span>
                                    <span className="title">
                                        Build your {isMisfitsTheme ? 'Misfits Market' : 'Imperfect Foods'} box
                                    </span>
                                    <span className="description">
                                        Your window is open to customize and add discounted grocery goods to your order.
                                    </span>
                                    <span className="location">
                                        https://www.{isMisfitsTheme ? 'misfitsmarket' : 'imperfectfoods'}.com
                                    </span>
                                    <span className="recurring">&lsquo;FREQ=WEEKLY;&rsquo;</span>
                                </span>
                                <span className="text-grey"> so you never miss a shopping window.</span>
                            </p>
                        )}
                    </div>
                </div>
            ),
        },
        {
            key: 'need-a-break',
            content: (
                <div className="overflow-hidden rounded-xl border border-black bg-white">
                    <img
                        src={`${BOYSENBERRY_S3_IMAGES}onboarding-step3${isMisfitsTheme ? '' : '-imperfectfoods'}.jpg`}
                        alt="Need a break?"
                        className="h-[200px] w-full object-cover md:h-[260px]"
                        onLoad={remeasureSlides}
                    />
                    <div className="flex flex-col items-center p-15 text-center">
                        <h3 className="mb-10 font-grotesk-bold text-body-mobile-md md:text-body-desktop-lg">
                            Need a break?
                        </h3>
                        <p className="text-body-mobile-xs text-grey md:text-body-desktop-sm">
                            You can skip a week, pause your subscription, or edit your plan in your account at any time.
                        </p>
                        <p className="mt-15 text-body-mobile-xs text-grey md:text-body-desktop-sm">
                            <span className="font-grotesk-bold">Reminder:</span> If you don&apos;t make any changes and
                            your cart meets your ${orderMin} order minimum, your box will be ordered and shipped
                            automatically each week.
                        </p>
                    </div>
                </div>
            ),
        },
    ];

    const handleNext = () => {
        if (selectedIndex === slides.length - 1) {
            closeOpenWindowModal();
        } else {
            scrollNext();
        }
    };

    const isLastSlide = selectedIndex === slides.length - 1;

    // `useEffect` runs after paint, so toggling transition there missed the same frame as `currentHeight`
    // changing — the height jumped with no transition. `indexJustChanged` applies `transition-[height]` on
    // that first render; `holdHeightTransition` (layout effect + timeout) keeps it through remeasure
    // re-renders and for the full 300ms.
    const prevSelectedIndexRef = useRef(selectedIndex);
    const indexJustChanged = prevSelectedIndexRef.current !== selectedIndex;
    prevSelectedIndexRef.current = selectedIndex;

    const [holdHeightTransition, setHoldHeightTransition] = useState(false);
    const isFirstSelectedIndexLayout = useRef(true);
    useLayoutEffect(() => {
        if (isFirstSelectedIndexLayout.current) {
            isFirstSelectedIndexLayout.current = false;
            return () => {};
        }
        setHoldHeightTransition(true);
        const id = window.setTimeout(() => setHoldHeightTransition(false), CAROUSEL_HEIGHT_TRANSITION_MS);
        return () => window.clearTimeout(id);
    }, [selectedIndex]);

    const animateCarouselHeight = indexJustChanged || holdHeightTransition;

    const content = (
        <div className="flex flex-col items-center py-10 md:py-0">
            <h2 className="text-body-lg mb-15 px-20 pt-10 text-center font-grotesk-bold md:pt-0 md:text-heading-desktop-xs">
                Welcome to {isMisfitsTheme ? 'Misfits Market' : 'Imperfect Foods'}!
            </h2>

            <div className="w-full">
                <div
                    className={`overflow-hidden${animateCarouselHeight ? ' transition-[height] duration-300 ease-out' : ''}`}
                    ref={emblaRef}
                    style={{ height: typeof currentHeight === 'number' ? `${currentHeight}px` : currentHeight }}
                >
                    <div className="flex items-start" ref={slidesContainerRef}>
                        {slides.map((slide, index) => (
                            <div
                                key={slide.key}
                                className="relative min-w-0 flex-[0_0_100%] px-20 md:px-30"
                                style={getSlideStyle(index)}
                                data-slide
                            >
                                {slide.content}
                            </div>
                        ))}
                    </div>
                </div>

                <div className="my-15 flex justify-center gap-10 px-20 md:my-20">
                    {slides.map((slide, index) => (
                        <button
                            key={slide.key}
                            type="button"
                            className={`h-[7px] w-[7px] rounded-full transition-colors ${
                                index === selectedIndex ? 'bg-black' : 'bg-grey-light'
                            }`}
                            onClick={() => scrollTo(index)}
                            aria-label={`Go to slide ${index + 1}`}
                        />
                    ))}
                </div>

                {isLastSlide ? (
                    <div className="mt-10 flex w-full flex-col items-center gap-10 px-20 pb-10 md:pb-0">
                        <Button
                            customClass="w-full bg-kale md:max-w-[335px]"
                            size="md"
                            variant="primary"
                            onClick={() => {
                                closeOpenWindowModal();
                                if (window.isNativeApp) {
                                    window.ReactNativeWebView?.postMessage(JSON.stringify({ navigate: 'Cart' }));
                                } else {
                                    setCartOpen(true);
                                }
                                trackEvent('open window onboarding cta click', {
                                    cta_label: 'view your personalized cart',
                                });
                            }}
                        >
                            View Your Personalized Cart
                        </Button>
                        <Button
                            customClass="w-full md:max-w-[335px]"
                            size="md"
                            variant="primary"
                            onClick={() => {
                                closeOpenWindowModal();
                                trackEvent('open window onboarding cta click', { cta_label: 'browse the shop' });
                                setCartOpen(false);
                            }}
                        >
                            Browse the Shop
                        </Button>
                    </div>
                ) : (
                    <div className="px-20 pb-10 md:pb-0">
                        <Button
                            customClass="mx-auto w-full bg-kale md:max-w-[335px]"
                            size="md"
                            variant="primary"
                            onClick={handleNext}
                        >
                            Next
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );

    if (mobile) {
        return (
            <Drawer
                open={openWindowOnboardingModalOpen}
                toggleDrawer={() => {}}
                anchor="bottom"
                sx={{
                    '& .MuiDrawer-paper': {
                        borderTopLeftRadius: '16px',
                        borderTopRightRadius: '16px',
                        backgroundColor: '#F5F0E8',
                        overflowY: 'hidden',
                    },
                }}
                ModalProps={{
                    disableEscapeKeyDown: true,
                }}
            >
                {content}
            </Drawer>
        );
    }

    return (
        <Modal
            open={openWindowOnboardingModalOpen}
            onClose={() => {}}
            xToClose={false}
            bgColor="bg-malt"
            includeHorizontalPadding={false}
            styles={{
                contentRoot:
                    'md:max-w-[500px]! lg:max-w-[500px]! rounded-2xl! outline-hidden! overflow-visible! overflow-y-hidden! bg-[#F5F0E8]',
            }}
        >
            {content}
        </Modal>
    );
}

export default OpenWindowOnboardingModal;
